import { Injectable, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { from, Subject } from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthService } from "../../datlaz/modules/auth";
import { DatlazToasterService } from "../../datlaz/services/datlaz-toaster.service";
import { Payload } from "../user-management/_models/payload.model";
import { CLOSE_ABNORMAL, CLOSE_PROTOCOL_ERROR, SOCKET_OPEN } from "./constants";
import { AnalyticsService } from "src/app/datlaz/modules/analytics/analytics.service";
import { SessionLogoutModalComponent } from "src/app/datlaz/modules/auth/session-logout-modal/session-logout-modal.component";

@Injectable({
  providedIn: "root",
})
export class WebSocketService implements OnInit {
  private socketsMap = new Map<string, WebSocket>();

  private openSocketSubject = new Subject();

  private closeSocketSubject = new Subject();

  // Add new subject for notification refresh
  private notificationReceivedSubject = new Subject<void>();
  public notificationReceived$ =
    this.notificationReceivedSubject.asObservable();

  /**
   * Return all previously registered sockets
   */
  initSocket() {
    return from(this.socketsMap.values());
  }

  constructor(
    private authService: AuthService,
    private datlazToaster: DatlazToasterService,
    private modalService: NgbModal,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {}

  private registerEvents(ws: WebSocket) {
    // Emitted when the connection is closed. code is a numeric value indicating the status code explaining why the connection has been closed. reason is a Buffer containing a human-readable string explaining why the connection has been closed.
    ws.onclose = (ev: CloseEvent) => {
      this.onClose(ev.code, ev.reason);
    };

    // Emitted when an error occurs. Errors may have a .code property, matching one of the string values defined below under WS Error Codes.
    ws.onerror = (ev: Event) => {
      this.onError(ev);
    };

    // Emitted when a message is received. data is the message content. isBinary specifies whether the message is binary or not.
    ws.onmessage = (ev: MessageEvent) => {
      this.onMessage(ev.data);
    };

    // Emitted when the connection is established.
    ws.onopen = (ev: Event) => {
      this.onOpen();
    };
  }

  private onOpen() {}

  private onMessage(unparsedData: any) {
    // Use the new Datlaz toaster with 30-second duration and click functionality
    if (unparsedData == null) {
      return;
    }

    if (unparsedData.includes("__pong__")) {
      return;
    }

    if (unparsedData.includes("connection_established")) {
      const data = JSON.parse(unparsedData);
      this.datlazToaster.info(
        "Bem-vindo a Datlaz",
        "Login realizado com sucesso",
        {
          duration: 30000, // 30 seconds
          redirectUrl: data.actionTarget, // Use actionTarget as redirect URL if available
          icon: data.icon, // Use custom icon if provided
        }
      );
      return;
    }

    const data = JSON.parse(unparsedData) as Payload;
    this.datlazToaster.info(data.title, data.content, {
      duration: 30000, // 30 seconds
      redirectUrl: data.actionTarget, // Use actionTarget as redirect URL if available
      icon: data.icon, // Use custom icon if provided
    });

    this.processReceivedWsMessage(data);

    // Emit notification received event to trigger unread count reload
    this.notificationReceivedSubject.next();
  }

  private onError(err: Event) {}

  private onClose(code: number, reason: string) {
    if (code === CLOSE_PROTOCOL_ERROR || code === CLOSE_ABNORMAL) {
      this.initDefaultWebSocketConnections();
    }
  }

  public sendMessage(ws: WebSocket, msg: any) {
    ws.send(msg);
  }

  public open(topic: string, token: string) {
    // TODO: Fix websocket
    this.socketsMap.set(
      topic,
      new WebSocket(
        `${environment.ecotxNotificationsWSS}/${topic}/?Authorization=${token}`
      )
    );
    this.registerEvents(this.socketsMap.get(topic));
    this.openSocketSubject.next(this.socketsMap.get(topic));

    setInterval(() => {
      this.ping(this.socketsMap.get(topic));
    }, 600000);
  }

  public close(topic) {
    this.closeSocketSubject.next(topic);
  }

  public initDefaultWebSocketConnections() {
    const loggedUserAccessToken = this.authService.getLoggedUserAccessToken();
    this.open("ws-notifications", loggedUserAccessToken);
  }

  public ping(ws: WebSocket) {
    if (ws.readyState === SOCKET_OPEN) {
      ws.send("__ping__");
    }
  }

  private processReceivedWsMessage(wsPayload: Payload) {
    if (wsPayload.action === "LOGOUT") {
      this.analyticsService.trackLogoutMultisession();
      const modalRef = this.modalService.open(SessionLogoutModalComponent);
      modalRef.result.then(
        () => {
          this.authService.logout();
        },
        () => {
          this.authService.logout();
        }
      );
    } else if (wsPayload.action === "MULTIPLE_SESSIONS_WARN") {
      this.analyticsService.trackWarnMultisession();
    }
  }
}
